import { inject, Injectable } from '@angular/core';
import { catchError, delay, lastValueFrom, tap } from 'rxjs';
import {
    ActionWidget,
    DescriptionWidget,
    DownloadWidget,
    ImagesWidget,
    InfoKeyValuesWidget,
    LinkWidget,
    MotiveWidget,
    QuotationWidget,
    SellNowProduct,
    StatusWidget,
    TimelineItem,
    TimelineWidget,
    TitleWidget,
    Widget
} from 'src/app/interfaces';
import { SellNowService } from '../api';
import { AlertController } from '@ionic/angular';

@Injectable({
    providedIn: 'root'
})
export class AppSellNowItemService {
    timelineItems: TimelineItem[] = [];
    product: SellNowProduct = null;
    serviceApi = inject(SellNowService);
    alertController = inject(AlertController);

    getItem(id: string) {
        return this.serviceApi.getSellNowTimeline(id).pipe(
            tap((timeline) => {
                this.timelineItems = timeline.items;
                this.product = timeline.product;
            }),
            catchError((error) => {
                throw error;
            })
        );
    }

    acceptPrice(id: string, email: string) {
        return this.serviceApi.acceptPrice(id).pipe(
            tap(async (timeline) => {
                this.timelineItems = timeline.items;
                this.product = timeline.product;

                const alert = await this.alertController.create({
                    subHeader: 'Verificiare email',
                    message: `Te rugăm să verifici emailul ${email}, pentru a semna actul adițional care confirmă noua sumă convenită.`,
                    buttons: ['Ok']
                });

                await alert.present();
            }),
            catchError((error) => {
                throw error;
            })
        );
    }

    async negotiatePrice(id: string) {
        const alert = await this.alertController.create({
            subHeader: 'Negociere preț',
            message: 'Introdu prețul pe care dorești să îl primești pentru produsul tău.',
            inputs: [
                {
                    type: 'number',
                    placeholder: 'Prețul tău'
                }
            ],
            buttons: [
                {
                    text: 'Anulează',
                    role: 'cancel'
                },
                {
                    text: 'Negociază',
                    role: 'confirm',
                    handler: async (data) => {
                        if (data[0]) {
                            console.log('Negociere preț', data[0]);
                            this.serviceApi
                                .negotiatePrice(id, { price: Number(data[0]) })
                                .pipe(
                                    tap((timeline) => {
                                        this.timelineItems = timeline.items;
                                        this.product = timeline.product;
                                    }),
                                    catchError((error) => {
                                        throw error;
                                    })
                                )
                                .subscribe();
                        }
                    }
                }
            ]
        });

        await alert.present();
    }

    refusePrice(id: string) {
        return this.serviceApi.refusePrice(id).pipe(
            tap((timeline) => {
                this.timelineItems = timeline.items;
                this.product = timeline.product;
            }),
            catchError((error) => {
                throw error;
            })
        );
    }

    confirmAddress(id: string, payload: { address: string }) {
        return this.serviceApi.confirmAddress(id, { address: payload.address }).pipe(
            tap((timeline) => {
                this.timelineItems = timeline.items;
                this.product = timeline.product;
            }),
            catchError((error) => {
                throw error;
            })
        );
    }

    isDescriptionWidget(widget: Widget): widget is DescriptionWidget {
        return widget.type === 'description';
    }

    isLinkWidget(widget: Widget): widget is LinkWidget {
        return widget.type === 'link';
    }

    isStatusWidget(widget: Widget): widget is StatusWidget {
        return widget.type === 'status';
    }

    isTitleWidget(widget: Widget): widget is TitleWidget {
        return widget.type === 'title';
    }

    isMotiveWidget(widget: Widget): widget is MotiveWidget {
        return widget.type === 'motive';
    }

    isInfoKeyValuesWidget(widget: Widget): widget is InfoKeyValuesWidget {
        return widget.type === 'infoKeyValues';
    }

    isImagesWidget(widget: Widget): widget is ImagesWidget {
        return widget.type === 'images';
    }

    isQuotationWidget(widget: Widget): widget is QuotationWidget {
        return widget.type === 'quotation';
    }

    isActionWidget(widget: Widget): widget is ActionWidget {
        return widget.type === 'action';
    }

    isDownloadWidget(widget: Widget): widget is DownloadWidget {
        return widget.type === 'download';
    }

    isTimelineWidget(widget: Widget): widget is TimelineWidget {
        return widget.type === 'timeline';
    }
}
